'use client'

import { useEffect, useState } from 'react'

import { useAuth } from '../contexts/AuthContext'

// Experiment: does a promo convert better as a dismissible banner under the
// header (control), or folded into the header CTA itself as a badge (unified)?
//
// Phones only. The banner costs a whole strip of vertical chrome on a small
// screen and is dismissible; the badge is permanent but quiet. Both arms point
// at the same destination, so the comparison is surface, not offer.
// The promo that applies to this visitor, in the same precedence the header's
// banner selector has always used. Lifted out so both arms read one source —
// otherwise the badge and the banner could disagree about what's on offer.
//
// `label` is deliberately numeric ("-20%", "$4.99"): it has to fit a ~40px pill
// in every locale, and digits need no translation.
export function useActiveOffer() {
  const { displayPlan } = useAuth()

  if (!displayPlan) return null

  if (displayPlan === 'Free') {
    return {
      id: 'upgrade_discount',
      label: '-20%',
      href: '/upgrade',
      percent: '20%',
    }
  }

  // Paid tiers get the gems sale. Note the href: the gem pill sends Plus and
  // Premium to /upgrade normally, but this offer is a gems discount, so the
  // badge has to keep the banner's /gems destination or the arms aren't
  // measuring the same journey.
  return {
    id: 'gems_discount',
    label: '-50%',
    href: '/gems',
    percent: '50%',
  }
}

// SETTLED 2026-08-12 — shipped `unified` on phones. The experiment ran 2026-08-01
// to 2026-08-12 (~164K users per arm) and found no significant difference on any
// metric: CTA clicks 2.81% vs 2.79% (p≈0.72), upgrade starts 0.809% vs 0.823%
// (p≈0.64), purchases 0.507% vs 0.484% (p≈0.39). At that base rate the test could
// only have resolved effects larger than ~14% relative, so read it as "no large
// difference" rather than "identical".
//
// With no measurable revenue difference, `unified` wins on cost: it drops a whole
// banner component and a strip of mobile vertical space. Note control was
// DIRECTIONALLY ahead on purchases (+4.6% relative, not significant) — if that
// turns out to be real, this is the line to revert.
//
// Desktop keeps the banner. It was never enrolled (the flag was only read below
// 768px), so there is no result for it and no basis for changing it here.
const OVERRIDE_KEY = 'pc_offer_arm'

// QA override, so an arm can be checked on a real device without waiting to be
// bucketed: append ?offer_arm=unified (or =control) once and it sticks for that
// browser; ?offer_arm=off clears it. Deliberately read BEFORE the flag, so an
// override applies to this browser only.
function readOverride() {
  if (typeof window === 'undefined') return null
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('offer_arm')
    if (fromUrl === 'off') {
      window.localStorage.removeItem(OVERRIDE_KEY)
      return null
    }
    if (fromUrl === 'unified' || fromUrl === 'control') {
      window.localStorage.setItem(OVERRIDE_KEY, fromUrl)
      return fromUrl
    }
    const stored = window.localStorage.getItem(OVERRIDE_KEY)
    return stored === 'unified' || stored === 'control' ? stored : null
  } catch {
    // Private mode / blocked storage — fall through to the real flag.
    return null
  }
}

export function useOfferVariant() {
  const [state, setState] = useState({ variant: 'control', isPhone: false })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-width: 767px)')

    const read = () => {
      const override = readOverride()
      if (override) {
        setState({ variant: override, isPhone: mq.matches })
        return
      }
      // Settled: phones get unified, desktop keeps the banner.
      setState({
        variant: mq.matches ? 'unified' : 'control',
        isPhone: mq.matches,
      })
    }

    read()
    mq.addEventListener('change', read)

    return () => {
      mq.removeEventListener('change', read)
    }
  }, [])

  return state
}
